import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState, useCallback } from "react";
import { fmtDateTime } from "../lib/format";
import { useRoles } from "../lib/useRoles";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Bell, Send, Users, History, CheckCircle2, XCircle } from "lucide-react";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";
import { useClientPerms } from "../lib/useClientPerms";
import { NotifyKindChips } from "../components/NotifyKindChips";

const NOTIFY = [
  ["notify_timesheet_events", "Timesheet alerts", "Submitted / returned / rejected / approved emails to approvers and submitters."],
  ["notify_pending_digest", "Pending-approval digest", "Weekday 08:00 (Toronto) digest of weeks still awaiting approval."],
  ["notify_due_by", "Due By reminders", "Day-before and day-of reminders, plus daily past-due alerts until the run is posted."],
  ["notify_pay_date", "Pay date reminders", "2 days before and on pay date when the run is not posted."],
  ["notify_remittance", "CRA remittance reminders", "Due-soon (7 days), due-today and overdue alerts to client admins and staff."],
  ["notify_overrides", "Override alerts", "Master unlock and time-punch corrections (who, what, why)."],
  ["notify_onboarding", "New hire onboarding", "A new hire completed the onboarding form and is waiting under Pending Employees for approval."],
  ["notify_wsib_rates", "WSIB rate statement reminder", "Nov 1 and Dec 1: update the WSIB class and risk band from the new premium rate statement (clients with a WSIB profile only)."],
  ["notify_payment_returned", "Direct deposit returned", "A Forte direct deposit (or the client funding debit) was returned by the bank — NSF, closed account, bad details."],
  ["notify_client_users", "Email client users", "Include this client's admins / preparers / MGT users as recipients (staff are always included; viewers never)."],
];

export default function Notifications({ embedded = false }) {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const canEdit = useClientPerms().canEdit("notifications");
  const roleBase = useRoles().base;
  const [info, setInfo] = useState(null);
  const [log, setLog] = useState([]);
  const [testTo, setTestTo] = useState("me");
  const [sending, setSending] = useState(false);
  const [lastTest, setLastTest] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const editRecipient = async (action, email, name, kinds) => {
    try {
      await api.post(`/companies/${companyId}/notification-recipients`, { action, email, name, kinds });
      toast.success(action === "add" ? `${email} added to recipients` : action === "remove" ? `${email} removed from recipients` : action === "set_kinds" ? `Notification types updated for ${email}` : `${email} restored`);
      if (action === "add") { setNewEmail(""); setNewName(""); }
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search).get("company");
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (qs && r.data.some((c) => c.company_id === qs)) setCompanyId(qs);
      else if (r.data[0]) setCompanyId(r.data[0].company_id);
    });
  }, []);

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/notification-recipients`).then((r) => setInfo(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get(`/companies/${companyId}/notification-log?limit=60`).then((r) => setLog(r.data.log || [])).catch(() => {});
  }, [companyId]);
  const tick = useRefreshTick();
  useEffect(() => { load(); }, [load, tick]);

  const save = async (patch) => {
    try {
      await api.put(`/companies/${companyId}/notification-settings`, patch);
      toast.success("Notification settings saved"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sendTest = async () => {
    setSending(true); setLastTest(null);
    try {
      const { data } = await api.post(`/companies/${companyId}/notification-test`, { to: testTo });
      setLastTest(data);
      (data.failed?.length ? toast.warning : toast.success)(`Test sent to ${data.sent} of ${data.attempted} recipient(s)`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const st = info?.settings || {};
  const allRecipients = info?.kinds?.[0]?.recipients || [];

  return (
    <div className="w-full" data-testid="notifications-page">
      {!embedded && <CompanyBreadcrumb label="Notifications" />}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          {embedded
            ? <h2 className="text-base md:text-lg font-bold tracking-tight flex items-center gap-2"><Bell className="h-5 w-5" /> Automatic Email Notifications</h2>
            : <h1 className="text-4xl font-black tracking-tight flex items-center gap-3"><Bell className="h-7 w-7" /> Automatic Email Notifications</h1>}
          <p className="text-sm text-muted-foreground mt-1">What PostWage emails automatically for this company, who receives it, and whether it is getting through. Add external addresses (bookkeeper, owner) or remove anyone below.</p>
        </div>
        {companies.length > 1 && (
          <select data-testid="notifications-company-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
            {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <section className="border border-border bg-white p-5" data-testid="notification-settings">
            <h2 className="font-bold tracking-tight mb-3">What gets sent</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {NOTIFY.map(([k, label, help]) => (
                <label key={k} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input data-testid={`notify-setting-${k}`} type="checkbox" className="mt-0.5 h-4 w-4 accent-black" checked={!!st[k]} disabled={!canEdit || !info} onChange={(e) => save({ [k]: e.target.checked })} />
                  <span><span className="font-medium">{label}</span><span className="block text-[11px] text-muted-foreground">{help}</span></span>
                </label>
              ))}
            </div>
          </section>

          <section className="border border-border bg-white" data-testid="notification-kinds">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2"><Users className="h-4 w-4" /><h2 className="font-bold tracking-tight">Who receives what</h2></div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-neutral-50"><th className="px-5 py-2">Notification</th><th className="px-5 py-2">Audience / schedule</th><th className="px-5 py-2">Status</th><th className="px-5 py-2">Recipients</th></tr></thead>
              <tbody>
                {(info?.kinds || []).map((k) => (
                  <tr key={k.kind} className="border-t border-border/60 align-top" data-testid={`notify-kind-${k.kind}`}>
                    <td className="px-5 py-2.5 font-medium">{k.label}</td>
                    <td className="px-5 py-2.5 text-xs text-muted-foreground">{k.audience}</td>
                    <td className="px-5 py-2.5"><span className={`text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${k.enabled ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-neutral-300 text-neutral-500"}`}>{k.setting ? (k.enabled ? "On" : "Off") : "Manual"}</span></td>
                    <td className="px-5 py-2.5 text-xs">{k.recipients.length ? k.recipients.map((r) => r.email).join(", ") : <span className="text-red-600">No recipients</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        <div className="space-y-6">
          <section className="border border-black bg-white p-5" data-testid="notification-test">
            <h2 className="font-bold tracking-tight flex items-center gap-2"><Send className="h-4 w-4" /> Send a test email</h2>
            <p className="text-xs text-muted-foreground mt-1">Confirms the mail provider can reach the inbox. Uses the same template and recipient rules as real notifications. Addresses that fail 3 sends in a row are removed automatically (use Restore to re-enable).</p>
            <select data-testid="notification-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="mt-3 w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="me">Just me</option>
              <option value="all">Everyone on the recipient list ({allRecipients.length})</option>
              {allRecipients.map((r) => <option key={r.email} value={r.email}>{r.email}</option>)}
            </select>
            <button data-testid="notification-test-send" onClick={sendTest} disabled={sending || !canEdit || !companyId} className="mt-3 w-full h-10 bg-black text-white text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">{sending ? "Sending…" : "Send test"}</button>
            {lastTest && (
              <div className="mt-3 text-xs space-y-1" data-testid="notification-test-result">
                <div className="font-medium">Sent {lastTest.sent} of {lastTest.attempted}</div>
                {lastTest.recipients.map((e) => {
                  const f = (lastTest.failed || []).find((x) => x.email === e);
                  return <div key={e} className={f ? "text-red-700" : "text-emerald-700"}>{f ? <XCircle className="inline h-3 w-3 mr-1" /> : <CheckCircle2 className="inline h-3 w-3 mr-1" />}{e}{f ? ` — ${f.error}` : ""}</div>;
                })}
              </div>
            )}
          </section>

          <section className="border border-border bg-white" data-testid="notification-log">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2"><History className="h-4 w-4" /><h2 className="font-bold tracking-tight">Recent sends</h2></div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-border/60">
              {log.length ? log.map((l, i) => (
                <div key={i} className="px-5 py-2.5 text-xs" data-testid="notification-log-row">
                  <div className="flex items-center justify-between gap-2"><span className="font-mono uppercase tracking-widest text-[9px] text-muted-foreground">{l.kind}</span><span className="font-mono tabular-nums text-muted-foreground">{fmtDateTime(l.at)}</span></div>
                  <div className="font-medium truncate" title={l.subject}>{l.subject}</div>
                  <div className={`${(l.failed || []).length ? "text-red-700" : "text-emerald-700"}`}>{l.sent}/{(l.recipients || []).length} delivered{(l.failed || []).length ? ` · ${l.failed.map((f) => f.email).join(", ")} failed` : ""}</div>
                </div>
              )) : <div className="px-5 py-6 text-xs text-muted-foreground">Nothing sent yet for this company.</div>}
            </div>
          </section>
        </div>
      </div>

      <section className="border border-border bg-white mt-6" data-testid="notification-users">
        <div className="px-5 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-bold tracking-tight">Email addresses in use</h2>
          {canEdit && (
            <form data-testid="notify-add-recipient-form" onSubmit={(e) => { e.preventDefault(); if (newEmail) editRecipient("add", newEmail, newName); }} className="flex items-center gap-2">
              <input data-testid="notify-add-recipient-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (optional)" className="border border-input px-2 py-1.5 text-xs w-36 focus:outline-none focus:ring-2 focus:ring-black" />
              <input data-testid="notify-add-recipient-email" type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="bookkeeper@client.ca" className="border border-input px-2 py-1.5 text-xs w-56 focus:outline-none focus:ring-2 focus:ring-black" />
              <button data-testid="notify-add-recipient-submit" type="submit" className="h-8 px-3 border border-black bg-black text-white text-xs font-medium hover:bg-neutral-800 transition-colors">+ Add recipient</button>
            </form>
          )}
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-neutral-50"><th className="px-5 py-2">User</th><th className="px-5 py-2">Role</th><th className="px-5 py-2">Receives</th><th className="px-5 py-2">Delivery health</th>{canEdit && <th className="px-5 py-2"></th>}</tr></thead>
          <tbody>
            {(info?.users || []).map((u) => (
              <tr key={u.email} className="border-t border-border/60" data-testid={`notify-user-${u.email}`}>
                <td className="px-5 py-2"><div className="font-medium">{u.name || "—"}</div><div className="text-xs font-mono text-muted-foreground">{u.email}</div></td>
                <td className="px-5 py-2 text-xs min-w-[20rem]">
                  <div className="uppercase font-mono">{u.role === "extra" ? "additional recipient" : u.role === "client" ? `client · ${u.client_role}` : `staff · ${u.staff_role || "master"}`}</div>
                  {(u.role === "extra" || u.role === "super_admin" || ["admin", "preparer", "mgt"].includes(roleBase("client", u.client_role))) && (
                    <div className="mt-1">
                      <NotifyKindChips id={u.email} kinds={u.kinds} options={info?.kind_options} canEdit={canEdit}
                        sourceNote={u.kinds_source === "staff" && u.kinds ? "(teammate default · set on Staff page)" : ""}
                        onChange={(next) => editRecipient("set_kinds", u.email, undefined, next)} />
                    </div>
                  )}
                </td>
                <td className="px-5 py-2 text-xs">{u.receives ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Yes</span> : <span className="inline-flex items-center gap-1 text-neutral-500"><XCircle className="h-3.5 w-3.5" /> No{u.why ? ` — ${u.why}` : ""}</span>}</td>
                <td className="px-5 py-2 text-xs">
                  {u.auto_muted && <div data-testid={`notify-auto-muted-${u.email}`} className="inline-block mb-1 text-[9px] font-mono uppercase tracking-widest border border-red-600 bg-red-50 text-red-700 px-1.5 py-0.5">Auto-removed · {u.auto_muted.failures} blocks · {String(u.auto_muted.at).slice(0, 10)}</div>}
                  {u.health ? <div className="text-red-700" title={u.health.at}>Last send failed: {u.health.last_error}</div> : <span className="text-muted-foreground">No failures recorded</span>}
                </td>
                {canEdit && <td className="px-5 py-2 text-right whitespace-nowrap relative z-10">
                  {u.muted ? <button data-testid={`notify-restore-${u.email}`} onClick={() => editRecipient("restore", u.email)} className="text-xs border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors">Restore</button>
                    : (u.receives || u.role === "extra") && <button data-testid={`notify-remove-${u.email}`} onClick={() => { if (window.confirm(`Stop sending notifications to ${u.email}?`)) editRecipient("remove", u.email); }} className="text-xs border border-border px-2 py-1 hover:border-red-600 hover:text-red-600 transition-colors">{u.role === "extra" ? "Delete" : "Remove"}</button>}
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  );
}
